import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import styles from './index.module.css';
import { buildPoAddressBlock } from '../../../../utils/poAddressRendering';
import { resolvePurchaseOrderRoute } from '../../../../utils/purchaseOrderRoute';
import {
  buildGeneratedPoShippingHeadline,
  removePhysicalLocationHeadline,
  resolveGeneratedPoShippingMethodLabel,
  resolveGeneratedPoRecipientName,
} from '../../../../utils/generatedPoShippingDisplay';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const MetaField = ({ label, children, className = '' }) => (
  <div className={`${styles.metaField} ${className}`.trim()}>
    <div className={styles.metaLabel}>{label}</div>
    <div className={styles.metaValue}>{children || '—'}</div>
  </div>
);

const AddressBlock = ({ label, contactName, lines }) => (
  <section className={styles.metaSection}>
    <div className={styles.metaLabel}>{label}</div>
    {contactName && <div className={`${styles.metaValue} ${styles.addressName}`}>{contactName}</div>}
    <div className={`${styles.metaValue} ${styles.addressLines}`}>
      {lines.length ? lines.map((line, index) => <div key={`${label}-${index}`}>{line}</div>) : '—'}
    </div>
  </section>
);

const norm = (s) => String(s || '').trim().toLowerCase();


const mergeAddressSnapshot = (primary, fallback) => {
  const primaryIsObject = primary && typeof primary === 'object';
  const fallbackIsObject = fallback && typeof fallback === 'object';
  if (primaryIsObject || fallbackIsObject) {
    return { ...(fallbackIsObject ? fallback : {}), ...(primaryIsObject ? primary : {}) };
  }
  return primary || fallback || null;
};

function formatIhd(dateLike) {
  if (!dateLike) return '';
  const s = String(dateLike);
  const onlyDate = s.includes('T') ? s.split('T')[0] : s;   // e.g. "2025-11-11"
  const d = new Date(`${onlyDate}T00:00:00Z`);              // force UTC midnight
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC'
  }).format(d);
}

// Pretty print account fallback
function formatShippingAccount(sa) {
  if (!sa) return '';

  if (norm(sa.Type ?? sa.type) === 'other') {
    return String(sa.AccountValue ?? sa.accountValue ?? sa.AccountType ?? sa.accountType ?? 'Other').trim();
  }

  // Accept nested or flat, any casing
  const tpa = sa.ThirdPartyShippingAccount || sa.thirdPartyShippingAccount || sa;

  const rawCourier = tpa.Courier ?? tpa.courier ?? tpa.Carrier ?? tpa.carrier ?? '';
  const rawName    = tpa.AccountName ?? tpa.accountName ?? tpa.Name ?? tpa.name ?? '';
  const rawNumber  = tpa.AccountNumber ?? tpa.accountNumber ?? tpa.Number ?? tpa.number ?? '';

  const pretty = (s) => String(s).trim().replace(/fed[\s-]*ex/i, 'FedEx');

  const courier = pretty(rawCourier);
  const name    = pretty(rawName);
  const number  = String(rawNumber).trim();

  // De-dupe if courier === name (old “Fed Ex — Fed Ex”)
  const head = (courier && name && courier.toLowerCase() === name.toLowerCase())
    ? courier
    : [courier, name && `— ${name}`].filter(Boolean).join('');

  if (head) return [head, number && ` (${number})`].filter(Boolean).join('');
  // Legacy fallback shapes like { AccountType, AccountValue }
  const type = sa.AccountType ?? sa.accountType ?? '';
  const val  = sa.AccountValue ?? sa.accountValue ?? '';
  return [type, val].filter(Boolean).join(' ');
}

// -------- In-house decorator detection helpers --------
const DECORATOR_IDS = [1, 2, 3];
const INHOUSE_NAMES = [
  "Elite Decorations",
  "Elite Decoration Dept (Soft Goods)",
  "Elite Art Department",
  "Elite Awards Department",
  "Elite Awards Departments",
];

const normalizePostal = (p) => String(p || '').replace(/\s+/g, '').toUpperCase();

// Edmonton office canonical info (relaxed checks for safety)
const isEliteOfficeAddress = (addr) => {
  const street = norm(addr?.street);
  const city = norm(addr?.city);
  const postal = normalizePostal(addr?.postal);
  const looksLikeStreet = street.includes('14703') && street.includes('118') && (street.includes('ave') || street.includes('avenue'));
  const isEdmonton = city === 'edmonton';
  const isPostal = postal === 'T5L2M7';
  return looksLikeStreet && isEdmonton && isPostal;
};

const isInHouseByName = (nameOrCompany) => {
  const s = norm(nameOrCompany);
  if (!s) return false;
  if (INHOUSE_NAMES.map(norm).includes(s)) return true;
  // loose match
  return s.includes('elite') && (s.includes('decor') || s.includes('award') || s.includes('art'));
};

const isEliteInHouse = (po, billingAddrObj) => {
  const byId = DECORATOR_IDS.includes(Number(po?.decoratorId));
  const bySupplierName = isInHouseByName(po?.supplierInfo?.supplierName);
  const byBillingName = isInHouseByName(billingAddrObj?.name) || isInHouseByName(billingAddrObj?.company);
  const byAddress = isEliteOfficeAddress(billingAddrObj);
  return byId || bySupplierName || byBillingName || byAddress;
};

// Treat as an internal/Elite PO (for showing/hiding Account and choosing method/code source)
const isElitePO = (po, billingAddrObj) => {
  const partyType = norm(po?.partyType || po?.poPartyType || po?.routeOriginType);
  const decorator = po?.decorator || po?.supplierInfo?.decorator;
  const byStableIdentity = partyType === 'decorator' || po?.decoratorId != null ||
    decorator?.isInternal === true || (
      decorator?.instanceId != null && po?.instanceId != null &&
      String(decorator.instanceId) === String(po.instanceId)
    );
  const typeStr = norm(po?.purchaseOrderType);
  const byType = typeStr.includes('elite');
  const bySupplier = norm(po?.supplierInfo?.supplierName).includes('elite');
  return byStableIdentity || byType || bySupplier || isEliteInHouse(po, billingAddrObj);
};
// ------------------------------------------------------

// Try to locate the decorator shipping block saved from DecoratorShippingForm
function resolveDecoratorShipping(po) {
  return (
    po?.decoratorFormData ||                       // ideal
    po?.supplierInfo?.decoratorFormData ||         // sometimes mirrored here
    po?.decoratorShipping ||                       // alternate canonical field
    po?.decoration?.shipping ||                    // nested on decoration
    po?.supplierInfo?.decoration?.shipping ||      // legacy mirror
    null
  );
}

function resolveAccountObject(po) {
  const decShip = resolveDecoratorShipping(po);

  // Try likely places that hold FedEx account info
  return (
    // most common: explicit third-party payload on the decorator block
    (decShip?.ThirdPartyShippingAccount ||
    decShip?.thirdPartyDetails ||
    // sometimes the "selected user" for our/client accounts carries the fields
    decShip?.shippingUser ||
    // legacy PO fields (old behavior)
    po?.shippingAccount ||
    po?.supplierInfo?.shippingAccount || null)
  );
}

// Resolve FedEx service code from expected places
function resolveFedExCode({ po, decShip, elitePO }) {
  const candidates = [];

  const push = (v) => {
    if (v && String(v).trim()) candidates.push(String(v).trim());
  };

  // Prefer different sources depending on PO type
  if (elitePO) {
    push(po?.supplierInfo?.fedexServiceCode);
    push(po?.supplierInfo?.shippingMethodCode);
    push(po?.supplierInfo?.carrierServiceCode);
  } else {
    push(decShip?.fedexServiceCode);
    push(decShip?.serviceCode);
    push(decShip?.shippingMethodCode);
    push(decShip?.shippingServiceCode);
    const su = decShip?.shippingUser; // could be Our/Client selection object
    if (su && /(fedex|fdx)/i.test(su?.Courier || su?.carrier)) {
      push(su?.ServiceCode || su?.serviceCode || su?.Service);
    }
    const tpa = decShip?.ThirdPartyShippingAccount || decShip?.thirdPartyDetails;
    if (tpa && /(fedex|fdx)/i.test(tpa?.Courier || tpa?.carrier)) {
      push(tpa?.ServiceCode || tpa?.serviceCode || tpa?.Service);
    }
  }

  // As a last resort, try to extract FEDEX_* from the chosen method text
  const methodText = elitePO ? po?.supplierInfo?.shippingMethod : decShip?.shippingMethod;
  const match = /FEDEX[_\-\s]?[A-Z0-9]+/i.exec(String(methodText || ''));
  if (match) push(match[0].toUpperCase().replace(/\s+/g, '_'));

  const uniq = [...new Set(candidates.filter(Boolean))];
  return uniq[0] || '';
}

// Humanize the decorator shipping account into a printable string
function prettyAccountFromDecorator(decShip, poFallback) {
  if (!decShip) {
    // fall back to classic PO fields
    return (
      formatShippingAccount(poFallback?.shippingAccount) ||
      formatShippingAccount(poFallback?.supplierInfo?.shippingAccount) ||
      '—'
    );
  }

  // Third-party
  if (decShip.ThirdPartyShippingAccount) {
    return formatShippingAccount(decShip.ThirdPartyShippingAccount);
  }

  if (decShip.shippingAccount === 'other') {
    return String(
      decShip.manualShippingAccount ||
      decShip.shippingAccountOther ||
      decShip.AccountValue ||
      decShip.AccountType ||
      'Other'
    ).trim();
  }
  if (decShip.thirdPartyDetails) {
    return formatShippingAccount(decShip.thirdPartyDetails);
  }

  // Our or Client account possibly with a selected user object
  if (['our', 'client'].includes(decShip.shippingAccount)) {
    const su = decShip.shippingUser; // might be an object or an id
    if (su && typeof su === 'object') {
      return formatShippingAccount(su);
    }
    return decShip.shippingAccount === 'our' ? 'Our Account' : 'Client Account';
  }

  if (decShip.shippingAccount === 'supplier') return 'Supplier Account';

  // Other / unknown string value
  if (typeof decShip.shippingAccount === 'string' && decShip.shippingAccount.trim()) {
    return decShip.shippingAccount.trim();
  }

  // Final fallback
  return (
    formatShippingAccount(poFallback?.shippingAccount) ||
    formatShippingAccount(poFallback?.supplierInfo?.shippingAccount) ||
    '—'
  );
}

const PurchaseDetails = ({ purchaseOrderDetails, note }) => {
  const po = purchaseOrderDetails || {};
  const type = po.purchaseOrderType; // kept for parity
  const normalizedNote = String(note || '').trim();

  // Addresses (prefer values on PO, then mirrored supplierInfo/customer info)
  const shippingAddrObj = mergeAddressSnapshot(
    po.shipTo,
    po?.supplierInfo?.shipTo || po?.shippingAddress
  );

  const billingAddrObj = mergeAddressSnapshot(
    po.billTo,
    po?.supplierInfo?.billTo || po?.billingAddress || po?.customerBillingAddress
  );

  // Resolve contact headline and address lines while preserving business/location name.
  const route = resolvePurchaseOrderRoute({
    purchaseOrder: po,
    order: po.order || po.salesOrder,
    customer: po.customer || po.salesOrder?.customer,
    decorator: po.decorator || po.supplierInfo?.decorator,
    shippingDestination: shippingAddrObj,
    internalShippingSettings: po.internalShippingSettings || po.supplierInfo?.internalShippingSettings,
  });
  const physicalAddrObj = route.fulfillment.physicalAddress || shippingAddrObj;
  const { contactName: resolvedShippingName, lines: resolvedShippingLines } = buildPoAddressBlock(po, physicalAddrObj, 'shipping');

  // Prefer decorator shipping data for method + account

  // Supplier PO vs Elite/Decoration PO rule for method:
  // - Supplier PO -> decShip.shippingMethod
  // - Elite/Decoration PO -> po.supplierInfo.shippingMethod
  const elitePO = isElitePO(po, billingAddrObj);
  const decShip = resolveDecoratorShipping(po);

  const ihdRaw =
    po?.resolvedInhandsDate ??
    po?.inhandsDate ??
    po?.supplierInfo?.inhandsDate ??
    decShip?.inHandsDate ??
    decShip?.inhandsDate ??
    null;
  const inhandsFirm = po?.inhandsDateIsFirm === true;

  // The PO snapshot already selects the supplier or decorator leg during
  // generation. Prefer it over a different leg's legacy decorator form data.
  const hasSavedLegMethod = Boolean(
    po?.supplierInfo?.shippingMethodLabel || po?.supplierInfo?.shippingMethod
  );
  const preferSavedLeg = elitePO || hasSavedLegMethod;
  const preferredLabels = preferSavedLeg
    ? [po?.supplierInfo?.shippingMethodLabel, decShip?.shippingMethodLabel, po?.shippingMethodLabel]
    : [decShip?.shippingMethodLabel, po?.supplierInfo?.shippingMethodLabel, po?.shippingMethodLabel];
  const preferredValues = preferSavedLeg
    ? [po?.supplierInfo?.shippingMethod, decShip?.shippingMethod, po?.shippingMethod]
    : [decShip?.shippingMethod, po?.supplierInfo?.shippingMethod, po?.shippingMethod];
  const shippingMethodOptions = [
    po?.shippingMethodOptions, po?.shipMethods,
    po?.supplierInfo?.shippingMethodOptions, po?.supplierInfo?.shipMethods,
    decShip?.shippingMethodOptions, decShip?.shipMethods,
  ].find(Array.isArray) || [];
  const method = resolveGeneratedPoShippingMethodLabel({
    labels: preferredLabels,
    values: preferredValues,
    options: shippingMethodOptions,
  });
  const shippingHeadline = buildGeneratedPoShippingHeadline({
    route,
    routeDisplay: po?.routeDisplay,
    methodLabel: method,
  });
  const savedRecipientName = resolveGeneratedPoRecipientName({ route, address: shippingAddrObj, methodLabel: method });
  const shippingName = savedRecipientName || shippingHeadline || resolvedShippingName;
  const shippingLines = savedRecipientName || shippingHeadline
    ? removePhysicalLocationHeadline(resolvedShippingLines, physicalAddrObj)
    : resolvedShippingLines;

  // FED EX CODE (shown when available)
  const fedexCode = resolveFedExCode({ po, decShip, elitePO });

  // ACCOUNT: prefer customerPo; else pretty string from decorator shipping; else fallback
  const accountPrettyRaw = po.customerPo?.trim()
    ? po.customerPo.trim()
    : prettyAccountFromDecorator(decShip, po);

  // Show Account only for supplier POs and when there is a real value
  const accountPretty = accountPrettyRaw && accountPrettyRaw !== '—' ? accountPrettyRaw : '';
  const accountObj = resolveAccountObject(po);

  const showAccount = !elitePO && !!accountPretty;
  // PROJECT #
  const projectNumber = po.projectNumber || po.orderId || '—';

  return (
    <section className={styles.headerContainer}>
      <div className={styles.poMetaGrid}>
        <section className={styles.metaSection}>
          <MetaField label="Ship Date Required">{po.shipDateRequired || 'To meet IHD'}</MetaField>
          <MetaField label="In Hands Date">
            {formatIhd(ihdRaw)}
            {inhandsFirm && <span className={styles.firmTag}><LocalFireDepartmentIcon /> FIRM</span>}
          </MetaField>
          <MetaField label="Shipping Method">
            {method || 'N/A'}
            {fedexCode && <div>FedEx code: {fedexCode}</div>}
          </MetaField>
        </section>

        <AddressBlock
          label="Shipping Address"
          contactName={shippingName}
          lines={shippingLines}
        />
        <section className={styles.metaSection}>
          <MetaField label="Project #">{projectNumber}</MetaField>
          <MetaField label="Supplier">{po?.supplierInfo?.supplierName || '—'}</MetaField>
        </section>

        <section className={styles.metaSection}>
          <MetaField label="PO #">{po?.poNumber || '—'}</MetaField>
        </section>

        <section className={styles.metaSection}>
          <MetaField label="PO Date">{formatDate(po?.createdAt)}</MetaField>
          <MetaField label="Currency">{po?.currency || '—'}</MetaField>
        </section>
      </div>

      {normalizedNote && (
        <div className={styles.poNoteRow}>
          <span className={styles.metaLabel}>Shipping Note:</span>
          <span className={styles.metaValue}>{normalizedNote}</span>
        </div>
      )}
    </section>
  );
};

export default PurchaseDetails;
